import React, { useEffect, useState, CSSProperties } from "react";

import { Dialog } from "primereact/dialog";
import { Sparklines, SparklinesBars, SparklinesLine } from "react-sparklines";
import { T } from "services/lang/LanguageService";
import DatasetLineChart from "../charts/dataset-line-chart/DatasetLineChart";
import SparkLineChart from "../sparkline-advanced/SparkLineChart";
import SparkLineLabel from "./SparkLineLabel";
import { ISparkLineHandlerProps, ValueLabel, ValuePosition } from "./SparkLineTypes";

const SparkLineHandler = (props: ISparkLineHandlerProps) => {
  const value = props.dataArray[props.dataArray.length - 1];
  const decimalPlaces = props.decimalPlaces || 0;

  const isVertical =
    props.generalProperties?.valuePosition === "top" ||
    props.generalProperties?.valuePosition === "bottom";

  const isReverse =
    props.generalProperties?.valuePosition === "bottom" ||
    props.generalProperties?.valuePosition === "right";

  let flexDirection;
  if (isVertical) {
    flexDirection = isReverse ? "column" : "column-reverse";
  } else {
    flexDirection = isReverse ? "row" : "row-reverse";
  }

  const [dialog, setDialog] = useState<boolean>(false);
  const [valueLabel, setValueLabel] = useState<ValueLabel>({
    disconnected: false,
    value,
    decimalPlaces,
    content: `${value?.toFixed(decimalPlaces)} ${props.unit ?? ""}`,
    bgColor: "rgba(255, 255, 255, 0.6)",
    textColor: "inherit",
  });

  let estimatedChars = 4;

  if (typeof valueLabel.content === "string") {
    estimatedChars = valueLabel.content.length;
  } else if (typeof valueLabel.value === "number") {
    estimatedChars =
      valueLabel.value.toFixed(props.decimalPlaces || 0).length + (props.unit?.length ?? 0) + 1;
  }

  useEffect(() => {
    if (!props.currentBiggestValueWidth || estimatedChars > props.currentBiggestValueWidth) {
      props.setCurrentBiggestValueWidth?.(estimatedChars);
    }
  }, [estimatedChars]);

  useEffect(() => {
    setValueLabel(prev => ({
      ...prev,
      value,
      decimalPlaces,
      content: `${value?.toFixed(decimalPlaces)} ${props.unit ?? ""}`,
    }));
  }, [value, decimalPlaces, props.unit]);

  let el;

  if (props.dataArray && props.dataArray.length !== 0) {
    el = (
      <div className="flex w-full h-full items-center justify-content-center">
        {props.advanced ? (
          <SparkLineChart
            dataArray={props.dataArray}
            advancedProps={props.advancedProps!}
            lineColor={props.lineColor}
            lineWidth={props.lineWidth}
            setValueLabel={setValueLabel}
            chartWidth={props.sparklineWidth}
            chartHeight={props.height}
            currentBiggestValueWidth={props.currentBiggestValueWidth}
          />
        ) : (
          <div
            style={{
              lineHeight: "2rem",
              width: props.sparklineWidth ? `${props.sparklineWidth}px` : "100%",
              height: props.height ? `${props.height}px` : "auto",
              display: "inline-block",
            }}
          >
            <Sparklines data={props.dataArray} height={30} preserveAspectRatio="none">
              <SparklinesLine
                style={{
                  stroke: props.lineColor ?? "#41c3f9",
                  fill: "none",
                  strokeWidth: props.lineWidth ?? 1,
                }}
              />
              <SparklinesBars style={{ fill: props.lineColor ?? "#41c3f9", fillOpacity: ".25" }} />
            </Sparklines>
          </div>
        )}
      </div>
    );
  } else {
    el = <h4>{T("word.no-data")}</h4>;
  }

  const identifiers = [
    props.dataset,
    props.advancedProps?.baseline?.usage === "dataset" && props.advancedProps?.baseline?.dataset,
    props.advancedProps?.maxLine?.usage === "dataset" && props.advancedProps?.maxLine?.dataset,
    props.advancedProps?.minLine?.usage === "dataset" && props.advancedProps?.minLine?.dataset,
  ].filter(Boolean) as string[];

  let currentLabelWidth = props.currentBiggestValueWidth
    ? `${props.currentBiggestValueWidth * 15}px`
    : "auto";

  if (
    props.isMultipleCharts &&
    (props.generalProperties?.valuePosition === ValuePosition.TOP ||
      props.generalProperties?.valuePosition === ValuePosition.BOTTOM)
  ) {
    currentLabelWidth = "100%";
  }

  const contentToRender = (
    <>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-evenly",
          width: "100%",
          position: "relative",
          height: `${props.height ? `${props.height}px` : "auto"}`,
        }}
        onDoubleClick={() => setDialog(true)}
      >
        {el}
      </div>

      {props.isMultipleCharts && props.generalProperties?.hideValues ? null : (
        <div style={{ width: currentLabelWidth }}>
          <SparkLineLabel valueLabel={valueLabel} setValueLabel={setValueLabel} {...props} />
        </div>
      )}

      <Dialog
        header={T("label.scada-tags-chart")}
        visible={dialog}
        style={{ width: "min(100vw, 1000px)", height: "500px" }}
        modal={false}
        onHide={() => {
          setDialog(false);
        }}
      >
        <DatasetLineChart
          title=""
          identifiers={identifiers}
          showDateSelection
          dynamicHeight
          chartDialogOpen={dialog}
        />
      </Dialog>
    </>
  );

  if (props.isMultipleCharts) {
    return (
      <div
        className="flex align-items-center"
        style={{
          flexDirection: (flexDirection as CSSProperties["flexDirection"]) ?? "column-reverse",
        }}
      >
        {contentToRender}
      </div>
    );
  }
  return contentToRender;
};

export default SparkLineHandler;
